import { useQuery } from "@tanstack/react-query";
import { useTheme } from "@mui/material/styles";
import styled from "@emotion/styled";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";

import { getMessageQuota } from "../apiService";

// ── Mock API call ──────────────────────────────────────────────
const fetchSmsUsage = async () => {
  await new Promise((r) => setTimeout(r, 400));
  // Try different values: 743, 1000, 1082
  const sent = 1082;
  return { sent };
};

// ── Constants ──────────────────────────────────────────────────
const STARTING_BALANCE = 10.0;
const COST_PER_SMS = 0.01;
const INCLUDED_SMS = Math.floor(STARTING_BALANCE / COST_PER_SMS);

// ── Styled components (Emotion) ───────────────────────────────
const Card = styled.div`
  width: 100%;
  padding: 20px 24px;
  border-radius: 12px;
  background: ${({ bg }) => bg};
  border: 1px solid ${({ borderColor }) => borderColor};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ color }) => color};
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const StatusTag = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 99px;
  white-space: nowrap;
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
`;

const BalanceAmount = styled.span`
  font-size: 28px;
  font-weight: 500;
  letter-spacing: -0.03em;
  color: ${({ color }) => color};
`;

const Dot = styled.span`
  font-size: 13px;
  color: ${({ color }) => color};
`;

const CountLabel = styled.span`
  font-size: 13px;
  color: ${({ color }) => color};

  strong {
    font-weight: 500;
    color: ${({ accent }) => accent};
  }
`;

const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 99px;
  background: ${({ trackBg }) => trackBg};
  overflow: hidden;
  position: relative;
`;

const Fill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ pct }) => Math.min(pct, 100)}%;
  border-radius: 99px;
  background: ${({ gradient }) => gradient};
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const OverflowFill = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: ${({ pct }) => pct}%;
  border-radius: 99px;
  background: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 3px,
    ${({ stripeColor }) => stripeColor} 3px,
    ${({ stripeColor }) => stripeColor} 6px
  );
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const FooterText = styled.span`
  font-size: 11px;
  color: ${({ color }) => color};
`;

const Skeleton = styled.div`
  height: ${({ height }) => height || "14px"};
  width: ${({ width }) => width || "100%"};
  border-radius: 8px;
  background: ${({ bg }) => bg};
  animation: pulse 1.5s ease-in-out infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

// ── Helpers ────────────────────────────────────────────────────
const formatBalance = (amount) => {
  const abs = Math.abs(amount).toFixed(2);
  return amount < 0 ? `-$${abs}` : `$${abs}`;
};

const getStatus = (balance) => {
  if (balance <= 0) return "overdrawn";
  if (balance <= 2) return "low";
  return "ok";
};

// ── Component ──────────────────────────────────────────────────
export default function SmsQuotaBanner() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const { data, isLoading } = useQuery({
    queryKey: ["smsUsage"],
    queryFn: getMessageQuota,
    staleTime: 15 * 60 * 1000,
  });

  const skeletonBg = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";

  if (isLoading || !data) {
    return (
      <Card bg={theme.palette.background.paper} borderColor={theme.palette.divider}>
        <Header>
          <HeaderLeft>
            <SmsOutlinedIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
            <Skeleton bg={skeletonBg} width="100px" height="13px" />
          </HeaderLeft>
        </Header>
        <Skeleton bg={skeletonBg} width="180px" height="28px" style={{ marginBottom: 16 }} />
        <Skeleton bg={skeletonBg} height="6px" />
      </Card>
    );
  }

  const { messagesSent } = data;
  const balance = STARTING_BALANCE - messagesSent * COST_PER_SMS;
  const pct = (messagesSent / INCLUDED_SMS) * 100;
  const overflowPct =
    pct > 100
      ? Math.min(((messagesSent - INCLUDED_SMS) / INCLUDED_SMS) * 100, 100)
      : 0;
  const status = getStatus(balance);

  const palette = {
    ok: {
      balance: theme.palette.success.main,
      gradient: "linear-gradient(90deg, #5DCAA5, #1D9E75)",
    },
    low: {
      balance: theme.palette.warning.dark,
      gradient: "linear-gradient(90deg, #fbbf24, #f59e0b)",
      tag: "Running low",
      tagBg: isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.1)",
      tagColor: theme.palette.warning.dark,
    },
    overdrawn: {
      balance: theme.palette.error.main,
      gradient: "linear-gradient(90deg, #E24B4A, #A32D2D)",
      stripeColor: isDark ? "rgba(163,45,45,0.4)" : "rgba(163,45,45,0.3)",
      tag: "Overage",
      tagBg: isDark ? "rgba(224,75,74,0.15)" : "rgba(224,75,74,0.1)",
      tagColor: theme.palette.error.main,
    },
  };

  const p = palette[status];

  return (
    <Card bg={theme.palette.background.paper} borderColor={theme.palette.divider}>
      <Header>
        <HeaderLeft>
          <SmsOutlinedIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
          <HeaderLabel color={theme.palette.text.secondary}>
            SMS balance
          </HeaderLabel>
        </HeaderLeft>
        {p.tag && (
          <StatusTag bg={p.tagBg} color={p.tagColor}>
            {p.tag}
          </StatusTag>
        )}
      </Header>

      <BalanceRow>
        <BalanceAmount color={p.balance}>
          {formatBalance(balance)}
        </BalanceAmount>
        <Dot color={theme.palette.text.disabled}>·</Dot>
        <CountLabel
          color={theme.palette.text.secondary}
          accent={theme.palette.text.primary}
        >
          <strong>{messagesSent.toLocaleString()}</strong> /{" "}
          {INCLUDED_SMS.toLocaleString()} messages sent
        </CountLabel>
      </BalanceRow>

      <Track trackBg={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}>
        <Fill pct={pct} gradient={p.gradient} />
        {overflowPct > 0 && (
          <OverflowFill pct={overflowPct} stripeColor={p.stripeColor} />
        )}
      </Track>

      <Footer>
        <FooterText color={theme.palette.text.hint}>
          ${COST_PER_SMS.toFixed(2)} / message
        </FooterText>
        <FooterText color={theme.palette.text.hint}>
          {INCLUDED_SMS.toLocaleString()} included
        </FooterText>
      </Footer>
    </Card>
  );
}